// Individual component exports
export { Carousel } from './Carousel';
export { CarouselRoot } from './CarouselRoot';
export { CarouselTrack } from './CarouselTrack';
export { CarouselItem } from './CarouselItem';
export { CarouselHeader } from './CarouselHeader';
export { CarouselTitle } from './CarouselTitle';
export { CarouselControls } from './CarouselControls';
export { CarouselSkipLinks } from './CarouselSkipLinks';
export { CarouselIndicators } from './CarouselIndicators';

// Export all types
export type * from './types';

// Export hooks
export { useCarouselNavigation } from './hooks/useCarouselNavigation';
export type {
  CarouselNavigationOptions,
  CarouselNavigationReturn,
  CarouselGapToken,
  CarouselStep,
} from './hooks/useCarouselNavigation';

// Import components for compound setup
import { Carousel } from './Carousel';
import { CarouselRoot } from './CarouselRoot';
import { CarouselTrack } from './CarouselTrack';
import { CarouselItem } from './CarouselItem';
import { CarouselHeader } from './CarouselHeader';
import { CarouselTitle } from './CarouselTitle';
import { CarouselControls } from './CarouselControls';
import { CarouselSkipLinks } from './CarouselSkipLinks';
import { CarouselIndicators } from './CarouselIndicators';

/**
 * Compound Carousel component following HALO's established pattern
 *
 * Provides both simple and compound API usage:
 *
 * Simple API:
 * ```tsx
 * import { Carousel } from '@/shared/halo/components/ui';
 *
 * <Carousel gap="md">
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Carousel>
 * ```
 *
 * Compound API:
 * ```tsx
 * import Carousel from '@/shared/halo/components/ui/Carousel';
 *
 * <Carousel.Root>
 *   <Carousel.Header>
 *     <Carousel.Title level={3}>Featured Properties</Carousel.Title>
 *     <Carousel.Controls />
 *   </Carousel.Header>
 *   <Carousel.Track gap="lg" snapAlign="start">
 *     <Carousel.Item width="280px">Content 1</Carousel.Item>
 *     <Carousel.Item width="280px">Content 2</Carousel.Item>
 *   </Carousel.Track>
 *   <Carousel.SkipLinks />
 * </Carousel.Root>
 * ```
 */
type CarouselCompoundType = typeof Carousel & {
  Root: typeof CarouselRoot;
  Track: typeof CarouselTrack;
  Item: typeof CarouselItem;
  Header: typeof CarouselHeader;
  Title: typeof CarouselTitle;
  Controls: typeof CarouselControls;
  SkipLinks: typeof CarouselSkipLinks;
  Indicators: typeof CarouselIndicators;
};

const CarouselCompound = Object.assign(Carousel, {
  Root: CarouselRoot,
  Track: CarouselTrack,
  Item: CarouselItem,
  Header: CarouselHeader,
  Title: CarouselTitle,
  Controls: CarouselControls,
  SkipLinks: CarouselSkipLinks,
  Indicators: CarouselIndicators,
}) as CarouselCompoundType;

export default CarouselCompound;
